/**
 * As avaliações de uma sessão encerrada, no registro dela (docs/spec/session-clock.md, "Avaliações").
 * Quem jogou dá a nota da sessão, e os mestres leem sem o nome; cada mestre dá a nota de cada jogador,
 * e o jogador lê, com o nome do mestre. Abrem no fim da sessão e fecham 7 dias depois; o servidor só
 * manda a cada um a parte que ele pode ver.
 */
import { useEffect, useRef, useState } from 'react';
import { api } from '../../api.js';
import { useT } from '../../i18n/index.js';
import { GAME_SESSION_RATING, type GameSessionRatingInput, type GameSessionRatings, type MySessionRating, type PlayerRatingView, type RatedPlayer, type SessionRatingView } from '../../shared.js';
import { describeError, useApp } from '../../store.js';
import { StarAverage, StarInput, Stars } from '../../ui/StarRating.js';
import { Icon } from '../../ui/icons.js';
import { toast } from '../../ui/Toaster.js';
import { averageOf, ratingRoles, ratingWindow } from './ratingsModel.js';

/**
 * `focus`: a pessoa foi levada até aqui (o ★ do relógio, o Ver de um aviso). O bloco rola para a vista
 * e a primeira escolha ganha o foco, uma vez por pedido.
 */
export function SessionRatings({ number, ratings, roomId, now, focus }: { number: number; ratings: GameSessionRatings | undefined; roomId: string | null; now: number; focus: number }) {
  const t = useT();
  const ref = useRef<HTMLElement>(null);
  const deadline = ratings ? ratingWindow(t, ratings.closesAt, now) : null;
  const { player, gm } = ratingRoles(ratings);
  const shown = !!deadline && (player || gm);
  useEffect(() => {
    if (!focus || !shown || !ref.current) return;
    ref.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
    ref.current.querySelector<HTMLElement>('input, button')?.focus({ preventScroll: true });
  }, [focus, shown]);
  if (!ratings || !deadline || !shown) return null;
  return (
    <section ref={ref} className="rd-sessions__section rd-sessions__ratings" aria-label={t('sessions.rating.title')}>
      <div className="rd-sessions__sectionHead">
        <h4 className="rd-sessions__subheading">{t('sessions.rating.title')}</h4>
        <span className={`rd-sessions__deadline${deadline.open ? '' : ' is-closed'}`}>{deadline.text}</span>
      </div>
      {player ? <MyRating number={number} mine={ratings.mine ?? null} roomId={roomId} open={deadline.open} /> : null}
      {ratings.aboutMe?.length ? <AboutMe ratings={ratings.aboutMe} /> : null}
      {ratings.session ? <FromPlayers ratings={ratings.session.ratings} players={ratings.session.players} /> : null}
      {ratings.players?.length ? <ForPlayers number={number} players={ratings.players} roomId={roomId} open={deadline.open} /> : null}
    </section>
  );
}

/** A nota que dei à sessão: o editor (sem nota ainda, ou no Editar) ou a leitura, com Editar e Tirar. */
function MyRating({ number, mine, roomId, open }: { number: number; mine: MySessionRating | null; roomId: string | null; open: boolean }) {
  const t = useT();
  const token = useApp((s) => s.token);
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  if (!token || !roomId) return null;
  if (!mine && !open) return <p className="rd-sessions__muted">{t('sessions.rating.notRated')}</p>;
  if (!mine || editing) {
    return (
      <div className="rd-sessions__rateBox">
        <p className="rd-sessions__rateAsk">{t('sessions.rating.ask')}</p>
        <RatingEditor
          initial={mine}
          label={t('sessions.rating.myLabel')}
          placeholder={t('sessions.rating.placeholder')}
          hint={t('sessions.rating.anonymous')}
          submit={t(mine ? 'sessions.save' : 'sessions.rating.send')}
          onSave={(rating) => api.rateSession(token, roomId, number, rating)}
          onDone={() => setEditing(false)}
          {...(mine ? { onCancel: () => setEditing(false) } : {})}
        />
      </div>
    );
  }
  const remove = () => {
    setRemoving(true);
    api.unrateSession(token, roomId, number).then(
      () => setRemoving(false),
      (failure: unknown) => {
        setRemoving(false);
        toast(describeError(failure), 'error');
      },
    );
  };
  return (
    <div className="rd-sessions__rateBox">
      <div className="rd-sessions__rateHead">
        <span className="rd-sessions__rateWho">{t('sessions.rating.mine')}</span>
        <Stars value={mine.stars} size={16} />
        {open ? (
          <span className="rd-sessions__rateActions">
            <button type="button" className="rd-sessions__editBtn" onClick={() => setEditing(true)}>
              <Icon name="edit" size={13} />
              {t('sessions.edit')}
            </button>
            <button type="button" className="rd-sessions__editBtn" disabled={removing} onClick={remove}>
              <Icon name="trash" size={13} />
              {t('sessions.rating.remove')}
            </button>
          </span>
        ) : null}
      </div>
      {mine.comment ? <p className="rd-sessions__rateText">{mine.comment}</p> : null}
      <p className="rd-sessions__rateHint">{t('sessions.rating.anonymous')}</p>
    </div>
  );
}

/** O que os mestres me deram, com o nome de cada um. */
function AboutMe({ ratings }: { ratings: PlayerRatingView[] }) {
  const t = useT();
  return (
    <div className="rd-sessions__rateGroup">
      <h5 className="rd-sessions__rateTitle">{t(ratings.length === 1 ? 'sessions.rating.aboutMe.one' : 'sessions.rating.aboutMe.many')}</h5>
      <ul className="rd-sessions__rateList">
        {ratings.map((rating) => (
          <li key={rating.gmId} className="rd-sessions__rateItem">
            <div className="rd-sessions__rateHead">
              <span className="rd-sessions__rateWho">{rating.gmName}</span>
              <Stars value={rating.stars} />
            </div>
            {rating.comment ? <p className="rd-sessions__rateText">{rating.comment}</p> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Para os mestres: as notas dos jogadores para a sessão, sem nome nem hora, com a média. */
function FromPlayers({ ratings, players }: { ratings: SessionRatingView[]; players: number }) {
  const t = useT();
  const average = averageOf(ratings);
  return (
    <div className="rd-sessions__rateGroup">
      <h5 className="rd-sessions__rateTitle">{t('sessions.rating.fromPlayers')}</h5>
      {!players ? (
        <p className="rd-sessions__muted">{t('sessions.rating.noPlayers')}</p>
      ) : (
        <>
          <p className="rd-sessions__rateSummary">
            {average !== null ? <StarAverage average={average} size={16} /> : null}
            <span>{ratings.length ? t(ratings.length === 1 ? 'sessions.rating.howMany.one' : 'sessions.rating.howMany.many', { n: ratings.length, total: players }) : t('sessions.rating.none')}</span>
          </p>
          {ratings.length ? (
            <ul className="rd-sessions__rateList">
              {ratings.map((rating, index) => (
                <li key={index} className="rd-sessions__rateItem">
                  <Stars value={rating.stars} />
                  {rating.comment ? <p className="rd-sessions__rateText">{rating.comment}</p> : null}
                </li>
              ))}
            </ul>
          ) : null}
          <p className="rd-sessions__rateHint">{t('sessions.rating.anonymousGm')}</p>
        </>
      )}
    </div>
  );
}

/** Para os mestres: cada jogador da sessão, com a minha nota (ou o Avaliar) e as dos outros mestres. */
function ForPlayers({ number, players, roomId, open }: { number: number; players: RatedPlayer[]; roomId: string | null; open: boolean }) {
  const t = useT();
  const token = useApp((s) => s.token);
  const me = useApp((s) => s.user?.id);
  const [editing, setEditing] = useState<string | null>(null);
  if (!token || !roomId) return null;
  return (
    <div className="rd-sessions__rateGroup">
      <h5 className="rd-sessions__rateTitle">{t('sessions.rating.players')}</h5>
      <p className="rd-sessions__rateHint">{t('sessions.rating.playersHint')}</p>
      <ul className="rd-sessions__ratePlayers">
        {players.map((player) => {
          const mine = player.ratings.find((rating) => rating.gmId === me) ?? null;
          const others = player.ratings.filter((rating) => rating.gmId !== me);
          const name = player.userName;
          return (
            <li key={player.userId} className="rd-sessions__ratePlayer">
              <div className="rd-sessions__ratePlayerHead">
                <span className="rd-sessions__avatar" aria-hidden="true">
                  {name.slice(0, 1).toUpperCase()}
                </span>
                <span className="rd-sessions__name">{name}</span>
                {editing === player.userId ? null : mine ? (
                  <>
                    <Stars value={mine.stars} />
                    {open ? (
                      <button type="button" className="rd-sessions__editBtn" aria-label={t('sessions.rating.editPlayer', { name })} onClick={() => setEditing(player.userId)}>
                        <Icon name="edit" size={13} />
                        {t('sessions.edit')}
                      </button>
                    ) : null}
                  </>
                ) : open ? (
                  <button type="button" className="rd-btn rd-btn--secondary rd-sessions__rateBtn" aria-label={t('sessions.rating.ratePlayer', { name })} onClick={() => setEditing(player.userId)}>
                    <Icon name="star" size={13} />
                    {t('sessions.rating.rate')}
                  </button>
                ) : (
                  <span className="rd-sessions__muted">{t('sessions.rating.notRatedPlayer')}</span>
                )}
              </div>
              {editing === player.userId ? (
                <RatingEditor
                  initial={mine}
                  label={t('sessions.rating.playerLabel', { name })}
                  placeholder={t('sessions.rating.playerPlaceholder', { name })}
                  submit={t('sessions.save')}
                  onSave={(rating) => api.ratePlayer(token, roomId, number, player.userId, rating)}
                  onDone={() => setEditing(null)}
                  onCancel={() => setEditing(null)}
                  {...(mine ? { onRemove: () => api.unratePlayer(token, roomId, number, player.userId) } : {})}
                />
              ) : mine?.comment ? (
                <p className="rd-sessions__rateText">{mine.comment}</p>
              ) : null}
              {others.map((rating) => (
                <div key={rating.gmId} className="rd-sessions__rateOther">
                  <div className="rd-sessions__rateHead">
                    <span className="rd-sessions__rateWho">{rating.gmName}</span>
                    <Stars value={rating.stars} size={12} />
                  </div>
                  {rating.comment ? <p className="rd-sessions__rateText">{rating.comment}</p> : null}
                </div>
              ))}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * As estrelas e o texto, com Enviar (ou Salvar), Cancelar e Tirar. Enviar fica desligado sem estrelas;
 * o Esc desiste. Depois de salvar, o registro relê pelo aviso da mesa, como as notas.
 */
function RatingEditor({
  initial,
  label,
  placeholder,
  hint,
  submit,
  onSave,
  onDone,
  onCancel,
  onRemove,
}: {
  initial: { stars: number; comment: string } | null;
  label: string;
  placeholder: string;
  hint?: string;
  submit: string;
  onSave: (rating: GameSessionRatingInput) => Promise<unknown>;
  onDone: () => void;
  onCancel?: () => void;
  onRemove?: () => Promise<unknown>;
}) {
  const t = useT();
  const [stars, setStars] = useState<number | null>(initial?.stars ?? null);
  const [comment, setComment] = useState(initial?.comment ?? '');
  const [saving, setSaving] = useState(false);
  const run = (action: () => Promise<unknown>) => {
    if (saving) return;
    setSaving(true);
    action().then(
      () => onDone(),
      (failure: unknown) => {
        setSaving(false);
        toast(describeError(failure), 'error');
      },
    );
  };
  return (
    <form
      className="rd-sessions__rateEditor"
      onSubmit={(e) => {
        e.preventDefault();
        if (stars) run(() => onSave({ stars, comment }));
      }}
      onKeyDown={(e) => {
        if (e.key !== 'Escape' || !onCancel) return;
        e.stopPropagation();
        onCancel();
      }}
    >
      <StarInput value={stars} onChange={setStars} label={label} disabled={saving} />
      <textarea
        className="rd-edit rd-edit--filled rd-sessions__rateInput"
        rows={3}
        value={comment}
        maxLength={GAME_SESSION_RATING.comment}
        placeholder={placeholder}
        aria-label={placeholder}
        disabled={saving}
        onChange={(e) => setComment(e.target.value)}
      />
      {hint ? <p className="rd-sessions__rateHint">{hint}</p> : null}
      <div className="rd-sessions__editorActions">
        <button type="submit" className="rd-btn rd-btn--action" disabled={saving || !stars}>
          {submit}
        </button>
        {onCancel ? (
          <button type="button" className="rd-btn rd-btn--secondary" disabled={saving} onClick={onCancel}>
            {t('sessions.cancel')}
          </button>
        ) : null}
        {onRemove ? (
          <button type="button" className="rd-btn rd-btn--secondary rd-sessions__rateRemove" disabled={saving} onClick={() => run(onRemove)}>
            <Icon name="trash" size={13} />
            {t('sessions.rating.remove')}
          </button>
        ) : null}
      </div>
    </form>
  );
}
